import { useState } from "react";

import {
  AutoInvoice,
  AUTO_INVOICE_STATUS_APPROVED_BY_SET,
  AUTO_INVOICE_STATUS_CANCELLATION_FAILED,
  AUTO_INVOICE_STATUS_CANCELLATION_REQUESTED,
  AUTO_INVOICE_STATUS_CANCELLED,
  AUTO_INVOICE_STATUS_ERROR,
  AUTO_INVOICE_STATUS_RESEND_REQUESTED,
  AUTO_INVOICE_STATUS_SET_UNKNOWN_ERROR,
  AUTO_INVOICE_STATUS_UPLOADED_TO_SET,
  formatAutoInvoiceNumber,
} from "../../models/auto-invoice";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCollapse,
  CTooltip,
} from "@coreui/react";
import { formatUTCDateTime } from "../../utils/dates";
import { formatToCurrency } from "../../currency/format";
import { CURRENCY_CHOICES } from "../../currency/available-currencies";
import AutoInvoiceCrudStatus from "./AutoInvoiceCrudStatus";
import AutoInvoiceCrudActions from "./AutoInvoiceCrudActions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import DocumentPrintButton from "../documents/DocumentPrintButton";
import { AUTO_INVOICES_RETRIEVE } from "../../auth/permissions";

interface AutoInvoiceCardProps {
  autoInvoice: AutoInvoice;
  onXMLClick: (inv: AutoInvoice) => void;
  onPDFClick: (inv: AutoInvoice) => void;
  onCancellationClick: (inv: AutoInvoice) => void;
  onUpdateClick: (inv: AutoInvoice) => void;
  onDeleteClick: (inv: AutoInvoice) => void;
  onConsultByCdcClick: (inv: AutoInvoice) => void;
  onResendClick: (inv: AutoInvoice) => void;
  onHistoryClick: (inv: AutoInvoice) => void;
}

export function AutoInvoiceCard({
  autoInvoice,
  onXMLClick,
  onPDFClick,
  onCancellationClick,
  onUpdateClick,
  onDeleteClick,
  onConsultByCdcClick,
  onResendClick,
  onHistoryClick,
}: AutoInvoiceCardProps) {
  const [open, setOpen] = useState(true);

  const getStatusColor = (status?: string): string => {
    switch (status) {
      case AUTO_INVOICE_STATUS_UPLOADED_TO_SET:
        return "warning";
      case AUTO_INVOICE_STATUS_ERROR:
        return "danger";
      case AUTO_INVOICE_STATUS_SET_UNKNOWN_ERROR:
        return "danger";
      case AUTO_INVOICE_STATUS_CANCELLATION_FAILED:
        return "danger";
      case AUTO_INVOICE_STATUS_APPROVED_BY_SET:
        return "success";
      case AUTO_INVOICE_STATUS_CANCELLATION_REQUESTED:
        return "light";
      case AUTO_INVOICE_STATUS_CANCELLED:
        return "info";
      case AUTO_INVOICE_STATUS_RESEND_REQUESTED:
        return "warning";
      default:
        return "info";
    }
  };

  let xmlButton = null;
  let pdfButton = null;

  if (autoInvoice.xmlPath !== undefined) {
    xmlButton = (
      <PermissionRequiredComponent permissionName={AUTO_INVOICES_RETRIEVE}>
        <CTooltip content="XML">
          <CButton
            className="text-white"
            color="info"
            onClick={() => {
              onXMLClick(autoInvoice);
            }}
          >
            <i className="fa fa-code"></i>
          </CButton>
        </CTooltip>
      </PermissionRequiredComponent>
    );
  }

  if (autoInvoice.pdfPath !== undefined) {
    pdfButton = (
      <PermissionRequiredComponent permissionName={AUTO_INVOICES_RETRIEVE}>
        <CTooltip content="PDF">
          <CButton
            className="text-white"
            color="danger"
            onClick={() => {
              onPDFClick(autoInvoice);
            }}
          >
            <i className="fa fa-file-pdf"></i>
          </CButton>
        </CTooltip>
      </PermissionRequiredComponent>
    );
  }

  return (
    <CCard className="mb-2">
      <CCardHeader
        className="d-flex justify-content-between align-items-center cursor-pointer"
        onClick={() => setOpen(!open)}
        color={getStatusColor(autoInvoice.status)}
      >
        <span>
          <strong>{formatAutoInvoiceNumber(autoInvoice)}</strong>
        </span>
        <span className="d-flex align-items-center gap-2">
          <strong>
            {formatUTCDateTime(new Date(autoInvoice.autoInvoiceDate!))}
          </strong>
          <span
            className="rounded-circle bg-white d-flex justify-content-center align-items-center ml-1"
            style={{ width: "22px", height: "22px" }}
          >
            <i
              className={`fa fa-chevron-${open ? "up" : "down"}`}
              style={{ color: "black", fontSize: "12px" }}
            />
          </span>
        </span>
      </CCardHeader>
      <CCollapse show={open}>
        <CCardBody>
          <p>
            <strong>Razón Social:</strong> {autoInvoice.taxPayerSocialName}
          </p>
          <p>
            <strong>RUC:</strong> {autoInvoice.taxPayerRuc}
          </p>
          <p>
            <strong>Monto:</strong>{" "}
            {formatToCurrency(autoInvoice.amount!, autoInvoice.currency!)}
          </p>
          {autoInvoice.totalDiscount && (
            <p>
              <strong>Descuento:</strong>{" "}
              {formatToCurrency(
                autoInvoice.totalDiscount,
                autoInvoice.currency!,
              )}
            </p>
          )}
          <p>
            <strong>Moneda:</strong>{" "}
            {CURRENCY_CHOICES.get(autoInvoice.currency!)}
          </p>
          <p>
            <strong>Timbrado Usado:</strong> {autoInvoice.stampIdentifier}
          </p>

          <p>
            <strong>Estado:</strong>{" "}
            <AutoInvoiceCrudStatus autoInvoice={autoInvoice} />
          </p>

          <div>
            <strong>Archivos:</strong>{" "}
            <td className="text-center">
              <CButtonGroup>
                {xmlButton}
                {pdfButton}
                {autoInvoice.pdfPath && autoInvoice.id !== undefined && <DocumentPrintButton kind="auto-invoice" documentId={autoInvoice.id} />}
              </CButtonGroup>
            </td>
          </div>
          <div>
            <strong>Acciones disponibles:</strong>{" "}
            <AutoInvoiceCrudActions
              autoInvoice={autoInvoice}
              onCancellationClick={onCancellationClick}
              onUpdateClick={onUpdateClick}
              onDeleteClick={onDeleteClick}
              onConsultByCdcClick={onConsultByCdcClick}
              onResendClick={onResendClick}
              onHistoryClick={onHistoryClick}
            />
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>
  );
}
